<template>
  <div class="button-flex button-type-1">
    <v-menu
      v-model="isMiniCartOpen"
      offset-y
      :close-on-content-click="false"
      :nudge-bottom="8"
      left
      min-width="420"
      max-width="420"
      content-class="mini-cart-menu"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          class="d-flex px-0"
          text
          min-height="60"
          min-width="60"
          z-index="8"
          v-bind="attrs"
          @click="handleCartClick"
          @mouseover="hoverItem"
          @mouseleave="unhoverItem"
        >
          <v-badge v-if="cartItems.length > 0" :content="cartItems.length" overlap bordered class="main-badge">
            <v-icon class="grey-2-hover-orange" size="30" aria-label="Carrito">
              {{ icons.mdiCart }}
            </v-icon>
          </v-badge>

          <v-icon v-else class="grey-2-hover-orange" size="30" aria-label="Carrito">
            {{ icons.mdiCart }}
          </v-icon>
        </v-btn>
        <div>
          <v-divider
            class="main-divider divider-hover-orange"
            :style="{ visibility: isHovered || isMiniCartOpen ? 'visible' : 'hidden' }"
          />
        </div>
      </template>

      <v-card class="mini-cart" width="420" max-height="520" elevation="8">
        <v-card-title class="py-3">
          <v-icon left class="mr-2">mdi-cart</v-icon>
          {{ t('Cart') }}
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-0 mini-cart__scroll">
          <v-data-table
            v-if="cartItems.length"
            :headers="headers"
            :items="cartItems"
            class="elevation-0 mini-table"
            dense
            :items-per-page="5"
            :footer-props="{ 'items-per-page-options': [5, 10, 20, -1] }"
          >
            <template v-slot:item.description="{ item }">
              <div class="ellipsis-cell">{{ getDescription(item) }}</div>
            </template>

            <template v-slot:item.quantity="{ item }">
              <div class="d-flex align-center justify-center">
                <v-btn icon small @click="decreaseQuantity(item.id)">
                  <v-icon small>mdi-minus</v-icon>
                </v-btn>
                <span class="mx-2">{{ item.quantity }}</span>
                <v-btn icon small @click="increaseQuantity(item.id)">
                  <v-icon small>mdi-plus</v-icon>
                </v-btn>
              </div>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn icon class="trash-btn" @click="removeItem(item.id)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>

            <template v-slot:body.append>
              <tr>
                <td :colspan="headers.length - 2"></td>

                <td class="text-center font-weight-bold">
                  <div>
                    {{ t('TOTAL') }}:
                    <span
                      style="
                        color: var(--v-primary-base);
                        font-size: 20px;
                        font-weight: bold;
                        margin-left: 10px;
                        display: inline-block;
                        min-width: 40px;
                        text-align: center;
                      "
                    >
                      {{ totalQuantity }}
                    </span>
                  </div>
                </td>

                <td class="text-center">
                  <v-btn icon color="grey lighten-2" @click="clearCart" :title="t('CleanCart')">
                    <v-icon dark>mdi-delete-sweep</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>

          <div v-else class="d-flex flex-column align-center justify-center py-8">
            <v-icon size="48" color="grey lighten-1">mdi-cart-off</v-icon>
            <div class="mt-2 grey--text text--darken-1">
              {{ t('NoProductsCart') }}
            </div>
          </div>
        </v-card-text>

        <v-divider v-if="cartItems.length" />

        <v-card-actions v-if="cartItems.length" class="px-4 py-3 d-flex align-center no-wrap">
          <v-btn
            :loading="loadingSave"
            text
            class="d-flex align-center justify-center background-color-1-60 btn-half"
            style="height: 50px !important; min-width: 0"
            @click="savePreOrder"
          >
            <v-icon left color="white" size="28" class="mr-2"> mdi-content-save </v-icon>
            <span class="font-size-14 font-weight-semibold white-color">
              {{ t('SaveOrder') }}
            </span>
          </v-btn>

          <v-btn
            v-if="canPlaceOrder"
            :loading="loadingPlace"
            text
            class="d-flex align-center justify-center background-color-1-60 btn-half ml-2"
            style="height: 50px !important; min-width: 0"
            @click="sendOrder"
          >
            <v-icon left color="white" size="28" class="mr-2"> mdi-cart-arrow-down </v-icon>
            <span class="font-size-14 font-weight-semibold white-color">
              {{ t('PlaceOrder') }}
            </span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { mdiCart } from '@mdi/js'
import store from '@/store'
import router from '@/router'
import { i18n } from '@/plugins/i18n'
import { useUtils } from '@/plugins/i18n/utils'
import LibClientManagementRs from 'lib-client-management-rs/src/lib-components/client-management-rs.vue'
import { EventBus } from '@/plugins/event-bus'
import sparePartManagement from 'lib-spare-part-management-vue/src/lib-components/spare-parts/sparePartManagement.js'
import LibSparePartManagementRs from 'lib-spare-part-management-rs/src/lib-components/spare-part-management-rs.vue'

const { t } = useUtils()
const locale = computed(() => i18n.locale)
const isHovered = ref(false)
const isMiniCartOpen = ref(false)
const { createOrder, userData } = sparePartManagement()

const realm = computed(() => {
  var id = window.location.hostname
  if (id === 'localhost') {
    id = 'gemsys-dev.geminys.com'
  }
  return id
})

async function getClient() {
  try {
    var client = await LibClientManagementRs.methods.getClient(null, realm.value)
    return client.data
  } catch (error) {
    console.log(error)
    return null
  }
}

const canPlaceOrder = computed(() => {
  return userData.value.roles?.includes('hermes-shopping-cart-management')
})

const cartItems = computed(() => store.state.app.cart.items || [])

const totalQuantity = computed(() => cartItems.value.reduce((sum, item) => sum + item.quantity, 0))

const icons = {
  mdiCart,
}

const headers = ref([
  { text: t('LIB.SPM.Description'), value: 'description' },
  {
    text: t('LIB.CM.Quantity'),
    value: 'quantity',
    sortable: false,
    align: 'center',
  },
  { text: '', value: 'actions', sortable: false },
])

function hoverItem() {
  isHovered.value = true
}
function unhoverItem() {
  isHovered.value = false
}

function handleCartClick() {
  if (!isMiniCartOpen.value) {
    isMiniCartOpen.value = true
  } else {
    isMiniCartOpen.value = false
    router.push({ name: 'cartView' })
  }
}

function increaseQuantity(id) {
  const item = cartItems.value.find(i => i.id === id)
  if (item) {
    store.commit('app/cart/addItem', { ...item, quantity: 1 })
  }
}

function decreaseQuantity(id) {
  store.commit('app/cart/decreaseItem', id)
}

function removeItem(id) {
  store.commit('app/cart/removeItem', id)
}

function clearCart() {
  store.commit('app/cart/clearCart')
}

function getDescription(item) {
  const lang = locale.value

  if (typeof item.description === 'string') {
    return item.description
  }

  if (typeof item.description === 'object' && item.description !== null) {
    return item.description[lang] || Object.values(item.description)[0]
  }
}
const incotermDAP = ref(false)
const loadingSave = ref(false)
const loadingPlace = ref(false)

async function sendOrder() {
  loadingPlace.value = true
  const client = await getClient()

  const formattedItems = cartItems.value.map(item => {
    let description

    if (typeof item.description === 'object') {
      description = item.description
    } else {
      description = { text: item.description }
    }

    return {
      reference: item.reference,
      description,
      type: item.type,
      quantity: item.quantity,
      comment: item.comment || null,
      accessDocument: item.accessDocument?.id || null,
      material: item.material || null,
    }
  })

  const orderPayload = {
    createdBy: client.name,
    createdAt: new Date().toISOString(),
    state: 'Sent',
    incotermDAP: incotermDAP.value,
    items: formattedItems,
  }

  let success = false
  try {
    const editingOrderNo = store.state.app.cart.editingOrderNo

    let result
    if (editingOrderNo) {
      orderPayload.orderNo = editingOrderNo
      result = await LibSparePartManagementRs.methods.editOrder(orderPayload, true)
    } else {
      result = await createOrder(orderPayload)
    }
    success = !!result
    if (success) clearCart()
  } catch (error) {
    console.error('Error sending order:', error)
  } finally {
    loadingPlace.value = false
    EventBus.$emit('update-notification-data-default', {
      visible: true,
      timeout: 3000,
      appendIcon: success ? 'done' : 'error',
      type: success ? 'success' : 'error',
      title: success ? i18n.t('LIB.SPM.SendSuccess') : i18n.t('LIB.SPM.SendError'),
    })
  }
}

async function savePreOrder() {
  loadingSave.value = true
  const client = await getClient()

  const formattedItems = cartItems.value.map(item => {
    let description

    if (typeof item.description === 'object') {
      description = item.description
    } else {
      description = { text: item.description }
    }

    return {
      reference: item.reference,
      description,
      type: item.type,
      quantity: item.quantity,
      comment: item.comment || null,
      accessDocument: item.accessDocument?.id || null,
      material: item.material || null,
    }
  })

  const orderPayload = {
    createdBy: client.name,
    createdAt: new Date().toISOString(),
    state: 'Waiting to send',
    incotermDAP: incotermDAP.value,
    items: formattedItems,
  }

  let success = false
  try {
    const editingOrderNo = store.state.app.cart.editingOrderNo

    let result
    if (editingOrderNo) {
      orderPayload.orderNo = editingOrderNo
      result = await LibSparePartManagementRs.methods.editOrder(orderPayload, true)
    } else {
      result = await createOrder(orderPayload)
    }
    success = !!result
    if (success) clearCart()
  } catch (error) {
    console.error('Error saving order:', error)
  } finally {
    loadingSave.value = false
    EventBus.$emit('update-notification-data-default', {
      visible: true,
      timeout: 3000,
      prependIcon: 'save',
      appendIcon: success ? 'done' : 'error',
      type: success ? 'success' : 'error',
      title: success ? i18n.t('LIB.SPM.PreorderSuccess') : i18n.t('LIB.SPM.PreorderError'),
    })
  }
}
</script>

<style scoped>
.main-badge >>> .v-badge__badge {
  background-color: #f57c00 !important;
  color: white !important;
}
.mini-cart__scroll {
  overflow-y: auto;
  overflow-x: hidden;
}
.mini-table .v-data-table__wrapper {
  overflow-x: hidden !important;
}
.mini-table table {
  table-layout: fixed;
  width: 100%;
}
.mini-table td,
.mini-table th {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ellipsis-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}
.no-wrap {
  flex-wrap: nowrap;
}
.btn-half {
  flex: 1 1 0;
  min-width: 0;
}
.btn-label-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  max-width: 100%;
}
</style>
